import { Download } from 'lucide-react'
import { Button } from '@/components/occurrences/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import type { Occurrence } from '@/types/occurrence'
import { PhotoPlaceholder } from './occurrence-ui'

export type PdfKind = 'ALERT' | 'LESSON'

/** Prévia de PDF corporativo (A4 vertical) — modelo versionado, dados restritos ocultos. */
export const PdfPreview = ({
	open,
	onOpenChange,
	kind,
	occ,
	revision,
	onConfirm,
}: {
	open: boolean
	onOpenChange: (v: boolean) => void
	kind: PdfKind
	occ: Occurrence
	revision: number
	onConfirm: () => void
}) => {
	const photos = occ.evidences
		.filter((e) => !e.deletedReason && e.confidentiality !== 'RESTRICTED')
		.slice(0, 3)
	const today = new Date().toLocaleDateString('pt-BR')
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
				<DialogHeader>
					<DialogTitle>
						{kind === 'ALERT' ? 'Alerta de comunicação' : 'Lição aprendida'} —
						prévia do PDF
					</DialogTitle>
				</DialogHeader>
				<div className="mx-auto w-full max-w-md space-y-3 border bg-white p-5 text-[12px] leading-snug text-black shadow-inner">
					<div className="flex items-start justify-between border-b-2 border-kaefer-pumpkin-orange pb-2">
						<div>
							<p className="text-sm font-bold">
								{kind === 'ALERT' ? 'ALERTA DE SEGURANÇA' : 'LIÇÃO APRENDIDA'}
							</p>
							<p>
								Filial: {occ.branch} · Ocorrência #{occ.number}
							</p>
						</div>
						<div className="text-right">
							<p>Rev. {String(revision).padStart(2, '0')}</p>
							<p>Emissão: {today}</p>
						</div>
					</div>
					<div className="grid grid-cols-3 gap-1.5">
						{photos.map((p) => (
							<PhotoPlaceholder key={p.id} tone={p.tone} className="h-16" />
						))}
					</div>
					{kind === 'ALERT' ? (
						<>
							<p>
								<b>O que aconteceu:</b> {occ.description}
							</p>
							<p>
								<b>Potencial:</b> {occ.potential} · <b>Classificação:</b>{' '}
								{occ.classification ?? occ.proposedClassification ?? '—'}
							</p>
							<p>
								<b>Causas:</b>{' '}
								{occ.investigation.causes
									.filter((c) => c.isRoot)
									.map((c) => c.text)
									.join('; ') || '—'}
							</p>
							<p>
								<b>Ações:</b>{' '}
								{occ.actions
									.slice(0, 3)
									.map((a) => a.description)
									.join('; ') || '—'}
							</p>
						</>
					) : (
						<>
							<p>
								<b>Serviço:</b> {occ.lesson?.service}
							</p>
							<p>
								<b>Atividade:</b> {occ.lesson?.activity}
							</p>
							<p>
								<b>Resumo:</b> {occ.lesson?.summary}
							</p>
							<p>
								<b>Aprendizados:</b> {occ.lesson?.learnings}
							</p>
							<p>
								<b>Recomendações:</b> {occ.lesson?.recommendations}
							</p>
							<p>
								<b>Palavras-chave:</b> {occ.lesson?.keywords.join(', ')}
							</p>
						</>
					)}
					<p className="border-t pt-2 text-[10px] text-neutral-500">
						Dados pessoais, médicos e confidenciais omitidos. Documento gerado
						pelo SSMA · QR Code de verificação (mock).
					</p>
				</div>
				<div className="flex justify-end gap-2">
					<Button variant="cancel" onClick={() => onOpenChange(false)}>
						Fechar
					</Button>
					<Button
						variant="primary"
						className="gap-1.5"
						onClick={() => {
							onConfirm()
							onOpenChange(false)
						}}
					>
						<Download className="size-4" /> Gerar e arquivar versão
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	)
}
